import { getLanguage, getMarket } from '../modules/i18n';
import type { IInitiable, ILink } from '@bjb/bjb-web-components/dist/mixins';
import type { JbAlert } from '@bjb/bjb-web-components/dist/components/jb-alert/jb-alert';
import type { JbButtonPrimary } from '@bjb/bjb-web-components/dist/components/jb-button-primary/jb-button-primary';
import type { JbPopup } from '@bjb/bjb-web-components/dist/components/jb-popup/jb-popup';

import '@bjb/bjb-web-components/dist/components/jb-alert/jb-alert.js';
import '@bjb/bjb-web-components/dist/components/jb-button-primary/jb-button-primary.js';
import '@bjb/bjb-web-components/dist/components/jb-popup/jb-popup.js';

// The payload of EXT:alerts' endpoint, see JuliusBaer\Alerts\Domain\Dto\Alert::getData().
type AlertButtonData = {
  closesTheOverlay: boolean,
  link: ILink | null,
  text: string,
};

type AlertAdditionalData = {
  currentPage?: number,
  outOfMarketEntry?: number | string,
  pages?: number[],
  repeats?: string,
  type?: string,
};

type AlertData = {
  additionalData?: AlertAdditionalData,
  buttons?: AlertButtonData[],
  id: string,
  texts: {
    content: string,
    legend: string,
    title: string,
  },
};

type AlertResponse = {
  initial: AlertData | null,
  prepared: AlertData[],
} | null;

const alertHandling = {
  now: Math.round(Date.now() / 1000),
  async init(alerts: AlertResponse) {
    if (!alerts) {
      return;
    }

    if (alerts.initial) {
      alertHandling.handleInitialAlert(alerts.initial);
    }
    if (alerts.prepared.length) {
      await alertHandling.handleOutOfMarketEntryAlerts(alerts.prepared);
      alertHandling.handleGateAlerts(alerts.prepared);
      alertHandling.handleMarketLeaveAlerts(alerts.prepared);
      alertHandling.handlePageEnterAlerts(alerts.prepared);
    }
  },
  handleInitialAlert(alertData: AlertData) {
    if (!alertData.additionalData || !this.shouldBeDisplayed(alertData.id, alertData.additionalData.repeats)) {
      return;
    }
    localStorage.setItem('alertDisplayed_' + alertData.id, this.now + '');
    this.openAlert(alertData);
  },
  handleGateAlerts(preparedAlerts: AlertData[]) {
    const gateAlerts = preparedAlerts.filter((alertToTest) => {
      if (!alertToTest.additionalData) {
        return false;
      }
      return alertToTest.additionalData.type === 'gateAlert';
    });

    if (gateAlerts.length > 0) {
      document.body.addEventListener('jb-link:clicked', (event) => {
        if (!event.detail.gateAlertUid) {
          return;
        }
        const matchingGateAlert = gateAlerts.find((singleAlert) => singleAlert.id === 'gateAlert_' + event.detail.gateAlertUid);
        if (matchingGateAlert) {
          event.stopPropagation();
          event.detail.clickEvent.preventDefault();
          this.openAlert(matchingGateAlert);
        }
      });
    }
  },
  handleMarketLeaveAlerts(preparedAlerts: AlertData[]) {
    const marketLeaveAlerts = preparedAlerts.filter((alertToTest) => {
      if (!alertToTest.additionalData) {
        return false;
      }
      return alertToTest.additionalData.type === 'marketLeaveAlert';
    });
    if (marketLeaveAlerts.length > 0) {
      document.body.addEventListener('jb-link:clicked', (event) => {
        const { leaveAlertPageId } = event.detail;
        if (!leaveAlertPageId) {
          return;
        }
        const matchingMarketLeaveAlert = marketLeaveAlerts.find((singleAlert) => singleAlert.additionalData?.pages?.includes(leaveAlertPageId));
        if (matchingMarketLeaveAlert) {
          event.stopPropagation();
          event.detail.clickEvent.preventDefault();
          this.pointFirstButtonAt(matchingMarketLeaveAlert, event.detail.linkTag);
          this.openAlert(matchingMarketLeaveAlert);
        }
      });
    }
  },
  async handleOutOfMarketEntryAlerts(preparedAlerts: AlertData[]) {
    // Show only on first visit to the website
    const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
    if (hasVisitedBefore) {
      // Early return for repeating visitors
      return;
    }
    // Mark that the user has now visited so alerts won't reappear on subsequent visits
    localStorage.setItem('hasVisitedBefore', '1');

    // Only consider alerts that define outOfMarketEntry
    const outOfMarketEntryAlerts = preparedAlerts.filter((alertToTest) => {
      return !(!alertToTest.additionalData || !alertToTest.additionalData.outOfMarketEntry);
    });
    if (outOfMarketEntryAlerts.length === 0) {
      return;
    }

    // Find and render one alert matching the user's market
    const currentMarket = await getMarket();
    const matchingAlert = outOfMarketEntryAlerts.find((singleAlert) => {
      const entryMarket = singleAlert?.additionalData?.outOfMarketEntry;
      if (entryMarket === undefined || entryMarket === null) {
        return false;
      }
      return String(entryMarket) === String(currentMarket);
    });

    if (matchingAlert) {
      this.openAlert(matchingAlert);
    }
  },

  handlePageEnterAlerts(preparedAlerts: AlertData[]) {
    const pageEnterAlerts = preparedAlerts.filter((alertToTest) => {
      if (!alertToTest.additionalData) {
        return false;
      }
      return alertToTest.additionalData.type === 'pageEnterAlerts';
    });
    if (pageEnterAlerts.length === 0) {
      return;
    }

    // initial alert
    if (document.referrer.indexOf(document.location.protocol + '//' + document.location.host) === -1) {
      pageEnterAlerts.forEach((pageEnterAlert) => {
        const { currentPage, pages } = pageEnterAlert.additionalData ?? {};
        if (currentPage === undefined || !pages?.includes(currentPage)) {
          return;
        }
        const firstButton = pageEnterAlert.buttons?.[0];
        if (firstButton) {
          firstButton.closesTheOverlay = true;
        }
        this.openAlert(pageEnterAlert);
      });
    }

    // alert when clicking link that enters pageEnterAlerts
    document.body.addEventListener('jb-link:clicked', (event) => {
      const { leaveAlertPageId } = event.detail;
      if (!leaveAlertPageId) {
        return;
      }
      const matchingPageEnterAlert = pageEnterAlerts.find((singleAlert) => {
        const { currentPage, pages } = singleAlert.additionalData ?? {};
        const linkTargetPageIsInAlertTargetPages = pages?.includes(leaveAlertPageId) ?? false;
        const currentPageIsInAlertTargetPages = currentPage !== undefined && (pages?.includes(currentPage) ?? false);
        return linkTargetPageIsInAlertTargetPages && !currentPageIsInAlertTargetPages;
      });
      if (!matchingPageEnterAlert) {
        return;
      }

      event.stopPropagation();
      event.detail.clickEvent.preventDefault();
      this.pointFirstButtonAt(matchingPageEnterAlert, event.detail.linkTag);
      this.openAlert(matchingPageEnterAlert);
    });
  },
  shouldBeDisplayed(id: string, repeats?: string) {
    if (repeats === 'always') {
      return true; // alert is configured to be always displayed => true
    }
    const lastDisplayed = localStorage.getItem('alertDisplayed_' + id);
    if (!lastDisplayed) {
      return true; // alert was never displayed before => true
    }
    if (repeats === 'never' || repeats === '') {
      return false; // alert was displayed before and is configured to never repeat => false
    }
    if (repeats !== 'daily') {
      console.error('Unrecognized value "' + repeats + '" as repeats property of pageAlert');
      return false;
    }

    return parseInt(lastDisplayed, 10) < this.now - 24 * 60 * 60; // true if lastDisplayed is at least 24h ago
  },
  // The alert's first button leads wherever the intercepted link pointed.
  pointFirstButtonAt(alertData: AlertData, linkTag: HTMLAnchorElement) {
    const link = alertData.buttons?.[0]?.link;
    const href = linkTag.getAttribute('href');
    if (link && href) {
      link.href = href;
    }
  },
  openAlert(alertData: AlertData) {
    (document.createElement('jb-popup') as JbPopup&IInitiable).init({
      childNodes: [
        (document.createElement('jb-alert') as JbAlert&IInitiable).init({
          props: {
            title: alertData.texts.title,
            content: alertData.texts.content,
            legend: alertData.texts.legend,
            showLegend: true,
          },
          childNodes: alertData.buttons?.map((singleButton) => {
            const button: JbButtonPrimary = (document.createElement('jb-button-primary') as JbButtonPrimary&IInitiable).init({
              props: {
                inverted: true,
                link: singleButton.link ?? undefined,
              },
            });
            button.setAttribute('slot', 'buttons');
            button.innerHTML = singleButton.text;
            if (singleButton.closesTheOverlay) {
              button.addEventListener('click', (e) => {
                e.preventDefault();
                button.closest('jb-popup')?.unstack();
              });
            }
            return button;
          }),
        }),
      ]
    }).stack();
  }
}

async function buildAlertsEndpoint(): Promise<string | null> {
  const endpointUrl = document.body.dataset.alertsEndpoint;
  const pageUid = document.body.dataset.pageUid;
  if (!endpointUrl || !pageUid) {
    return null;
  }

  const [language, market] = await Promise.all([getLanguage(), getMarket()]);

  const replaced = endpointUrl
    .replace(':pageUid', encodeURIComponent(pageUid));

  const url = new URL(replaced, window.location.origin);
  if (language) {
    url.searchParams.set('L', language);
  }
  if (market) {
    url.searchParams.set('market', market);
  }

  return url.toString();
}

window.addEventListener('DOMContentLoaded', () => {
  buildAlertsEndpoint()
    .then((endpoint) => {
      if (!endpoint) {
        return;
      }

      return fetch(endpoint, { credentials: 'include' })
        .then((response) => (response.ok ? response.json() : null))
        .then((alerts: AlertResponse) => alertHandling.init(alerts));
    })
    .catch((error) => {
      console.error('Error fetching alerts:', error);
    });
});
